import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import QuizIcon from '@mui/icons-material/Quiz';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { toast } from 'react-toastify';
import TimezoneUtils from '../utils/timezoneUtils';

const formatDate = (value) => {
  if (!value) return '—';
  const date = typeof value === 'string' || typeof value === 'number' ? new Date(value) : value;
  return date.toLocaleString();
};

const clip = (text, length = 180) => {
  if (!text) return '';
  return text.length > length ? `${text.slice(0, length)}…` : text;
};

const stripQuestionPrefixFromAnswer = (answer) => {
  if (!answer) return '';
  const raw = String(answer).trim();
  const qPrefix = 'Question: ';
  if (!raw.toLowerCase().startsWith(qPrefix.toLowerCase())) return raw;
  const afterPrefix = raw.slice(qPrefix.length);
  const split = afterPrefix.indexOf('\n\n');
  if (split === -1) return raw;
  return afterPrefix.slice(split + 2).trim() || raw;
};

const getQaPairs = (history) => {
  const items = history?.helpQa || [];
  return items
    .filter((item) => item && (item.question || item.answer))
    .map((item, index) => ({
      ...item,
      index,
      displayAnswer: stripQuestionPrefixFromAnswer(item.answer),
    }));
};

const buildTimeline = (history) => {
  const interviewer = (history?.transcripts?.interviewer || []).map((entry) => ({
    ...entry,
    speaker: 'Interviewer',
    sortOrder: entry.timestamp ?? entry.order ?? 0,
  }));

  const user = (history?.transcripts?.user || []).map((entry) => ({
    ...entry,
    speaker: 'You',
    sortOrder: entry.timestamp ?? entry.order ?? 0,
  }));

  return [...interviewer, ...user].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
};

const InterviewCallsTab = () => {
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => TimezoneUtils.getTodayForInput());
  const [resumeNames, setResumeNames] = useState({});
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const [titleSaving, setTitleSaving] = useState(false);
  const [showFullJobDescription, setShowFullJobDescription] = useState(false);
  const [detailTab, setDetailTab] = useState('qa');
  const [expandedQa, setExpandedQa] = useState({});

  const buildDateParams = useCallback((dateValue) => {
    if (!dateValue) {
      return {};
    }

    const fromDate = new Date(dateValue);
    return {
      startDate: TimezoneUtils.formatForAPI(TimezoneUtils.startOfDayUTC(fromDate)),
      endDate: TimezoneUtils.formatForAPI(TimezoneUtils.endOfDayUTC(fromDate)),
    };
  }, []);

  const fetchHistories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/chat-history`, {
        params: buildDateParams(selectedDate),
      });
      setHistories(response.data || []);
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to load chat history';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [buildDateParams, selectedDate]);

  const fetchResumes = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/resumes`);
      const map = {};
      (response.data || []).forEach((res) => {
        const label =
          res.nickname ||
          res.content?.personal_info?.name ||
          res.content?.profile ||
          'Resume';
        map[res._id] = label;
      });
      setResumeNames(map);
    } catch (error) {
      console.warn('Failed to fetch resumes for labels', error);
    }
  };

  const getResumeLabel = (resumeId) => {
    if (!resumeId) return '';
    return resumeNames[resumeId] || resumeId;
  };

  const openHistory = (history) => {
    setSelectedHistory(history);
    setDialogOpen(true);
    setEditingTitle(history.title || history.interviewId || '');
    setShowFullJobDescription(false);
    setExpandedQa({});
    const hasQa = getQaPairs(history).length > 0;
    setDetailTab(hasQa ? 'qa' : 'transcript');
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedHistory(null);
    setEditingTitle('');
    setTitleSaving(false);
    setShowFullJobDescription(false);
    setExpandedQa({});
  };

  const handleQaAccordionChange = (qaIndex) => (_, isExpanded) => {
    setExpandedQa((prev) => ({ ...prev, [qaIndex]: isExpanded }));
  };

  const saveTitle = async () => {
    if (!selectedHistory) return;
    const newTitle = editingTitle.trim();
    setTitleSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/chat-history`, {
        interviewId: selectedHistory.interviewId,
        title: newTitle,
      });
      setHistories((prev) =>
        prev.map((item) =>
          item.interviewId === selectedHistory.interviewId ? { ...item, title: newTitle } : item
        )
      );
      setSelectedHistory((prev) => (prev ? { ...prev, title: newTitle } : prev));
      toast.success('Title updated');
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to save title';
      toast.error(message);
    } finally {
      setTitleSaving(false);
    }
  };

  useEffect(() => {
    fetchResumes();
  }, []);

  useEffect(() => {
    fetchHistories();
  }, [fetchHistories]);

  const filteredHistories = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return histories;
    return histories.filter((item) => {
      const qaText = (item.helpQa || [])
        .map((qa) => `${qa.question || ''} ${qa.answer || ''}`)
        .join(' ')
        .toLowerCase();
      return (
        (item.title || '').toLowerCase().includes(term) ||
        (item.interviewId || '').toLowerCase().includes(term) ||
        (item.jobDescription || '').toLowerCase().includes(term) ||
        (item.additionalInfo || '').toLowerCase().includes(term) ||
        qaText.includes(term)
      );
    });
  }, [histories, filter]);

  return (
    <Box sx={{ width: '80%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
        <TextField
          label="Date"
          type="date"
          size="small"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <Button
          onClick={fetchHistories}
          startIcon={<RefreshIcon />}
          variant="outlined"
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          <TextField
            size="small"
            label="Search by interview, job, or notes"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            fullWidth
          />
          <Chip
            icon={<PersonIcon />}
            label={`${filteredHistories.length} interview${filteredHistories.length === 1 ? '' : 's'}`}
            color="primary"
            variant="outlined"
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : filteredHistories.length === 0 ? (
          <Typography color="text.secondary">
            No interview calls for {selectedDate || 'this date'}.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {filteredHistories.map((history) => {
              const title = history.title || history.interviewId || 'Interview';
              const qaCount = getQaPairs(history).length;
              return (
                <Box
                  key={history._id || history.interviewId}
                  onClick={() => openHistory(history)}
                  sx={{
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: 'background.paper',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main} inset`,
                    },
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {title}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexShrink={0} flexWrap="wrap">
                      {qaCount > 0 && (
                        <Chip
                          size="small"
                          icon={<QuizIcon />}
                          label={`${qaCount} Q&A`}
                          variant="outlined"
                          color="secondary"
                        />
                      )}
                      <Chip
                        size="small"
                        icon={<DescriptionIcon />}
                        label={history.resumeId ? getResumeLabel(history.resumeId) : 'No resume'}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        icon={<AccessTimeIcon />}
                        label={formatDate(history.updatedAt || history.createdAt || history.startedAt)}
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>
          {selectedHistory ? selectedHistory.title || selectedHistory.interviewId : 'Interview'}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            maxHeight: '70vh',
            overflowY: 'auto',
          }}
        >
          {selectedHistory && (
            <>
              <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Title"
                  value={editingTitle}
                  onChange={(e) => setEditingTitle(e.target.value)}
                  fullWidth
                />
                <Button variant="contained" onClick={saveTitle} disabled={titleSaving}>
                  {titleSaving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                {selectedHistory.resumeId && (
                  <Chip
                    size="small"
                    icon={<DescriptionIcon />}
                    label={getResumeLabel(selectedHistory.resumeId)}
                  />
                )}
                <Chip
                  size="small"
                  icon={<AccessTimeIcon />}
                  label={`Updated ${formatDate(selectedHistory.updatedAt || selectedHistory.createdAt || selectedHistory.startedAt)}`}
                />
              </Stack>
              {selectedHistory.jobDescription && (
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">Job Description</Typography>
                    <Button size="small" onClick={() => setShowFullJobDescription((prev) => !prev)}>
                      {showFullJobDescription ? 'Hide' : 'Show'}
                    </Button>
                  </Stack>
                  {showFullJobDescription ? (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        borderRadius: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        maxHeight: 240,
                        overflowY: 'auto',
                        backgroundColor: 'background.default',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedHistory.jobDescription}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {clip(selectedHistory.jobDescription, 180)}
                    </Typography>
                  )}
                </Box>
              )}
              {selectedHistory.additionalInfo && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Notes</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedHistory.additionalInfo}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ mb: 2 }} />
              <Tabs
                value={detailTab}
                onChange={(_, value) => setDetailTab(value)}
                sx={{ mb: 2 }}
              >
                <Tab
                  value="qa"
                  label={`Questions & AI Answers (${getQaPairs(selectedHistory).length})`}
                />
                <Tab value="transcript" label="Full Transcript" />
              </Tabs>
              {detailTab === 'qa' ? (
                getQaPairs(selectedHistory).length === 0 ? (
                  <Typography color="text.secondary">
                    No AI answers yet. Use Help during an interview to generate Q&A pairs.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {getQaPairs(selectedHistory).map((qa) => (
                      <Accordion
                        key={`qa-${selectedHistory._id || selectedHistory.interviewId}-${qa.index}`}
                        expanded={Boolean(expandedQa[qa.index])}
                        onChange={handleQaAccordionChange(qa.index)}
                        disableGutters
                        sx={{
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          '&:before': { display: 'none' },
                          borderRadius: '8px !important',
                          overflow: 'hidden',
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ pr: 1, width: '100%' }}>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                              <QuizIcon fontSize="small" color="primary" sx={{ mt: 0.25 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {qa.question || '(Question not captured)'}
                                </Typography>
                                {qa.timestamp && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    {formatDate(qa.timestamp)}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                            {qa.displayAnswer || '(No answer generated)'}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Stack>
                )
              ) : (
                <List dense>
                  {buildTimeline(selectedHistory).map((message, idx) => (
                    <ListItem
                      key={`${selectedHistory._id || selectedHistory.interviewId}-full-${idx}`}
                      alignItems="flex-start"
                      sx={{ py: 0.5 }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            <strong>{message.speaker}:</strong> {message.text}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(message.timestamp || selectedHistory.updatedAt)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InterviewCallsTab;
